/**
 * useUptimeData.ts — Hook for nself-uptime-monitor plugin (port 3831)
 *
 * Purpose:    Fetch and manage state for the Uptime Monitor dashboard.
 * Inputs:     None (reads from plugin backend port 3831)
 * Outputs:    { state, data, error, refresh, addEndpoint }
 * Constraints: Client-side only; uses pluginFetch; AsyncState enum for UI
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useCallback, useEffect, useState } from 'react'
import { pluginFetch } from '../lib/plugin-client'
import type { SentryError } from '../lib/sentry-error'
import type { AsyncState } from '../components/AsyncScreen'

const PORT = 3831

export interface UptimeEndpoint {
  id: string
  url: string
  name: string
  interval: number
  status: 'up' | 'down' | 'degraded' | 'unknown'
  latencyMs: number | null
  lastChecked: string | null
}

export interface UptimeAlert {
  id: string
  endpointId: string
  endpointName: string
  type: 'down' | 'degraded' | 'recovered'
  message: string
  triggeredAt: string
}

export interface UptimeData {
  endpoints: UptimeEndpoint[]
  alerts: UptimeAlert[]
}

export interface AddEndpointPayload {
  url: string
  name: string
  interval: number
}

export function useUptimeData() {
  const [state, setState] = useState<AsyncState>('loading')
  const [data, setData] = useState<UptimeData | null>(null)
  const [error, setError] = useState<SentryError | undefined>()

  const load = useCallback(async () => {
    setState('loading')
    setError(undefined)
    if (!navigator.onLine) {
      setState('offline')
      return
    }
    const result = await pluginFetch<UptimeData>(PORT, '/api/uptime/summary')
    if (result.ok) {
      setData(result.value)
      setState(
        result.value.endpoints.length === 0 &&
          result.value.alerts.length === 0
          ? 'empty'
          : 'success',
      )
    } else {
      const e = result.error
      if (e.type === 'bundle_required') setState('permission-denied')
      else if (e.type === 'rate_limit') setState('rate-limited')
      else if (e.type === 'plugin_offline') setState('offline')
      else setState('error')
      setError(e)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const addEndpoint = useCallback(
    async (payload: AddEndpointPayload): Promise<{ ok: boolean; error?: string }> => {
      const result = await pluginFetch<UptimeEndpoint>(PORT, '/api/uptime/endpoints', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if (result.ok) {
        await load()
        return { ok: true }
      }
      return { ok: false, error: result.error.message }
    },
    [load],
  )

  return { state, data, error, refresh: load, addEndpoint }
}
